// Compact navigation map with directional position indicator

import React, { useEffect, useMemo, useRef } from 'react';

interface PathNetworkMiniMapProps {
  pathNetwork: number[][][];
  currentPosition?: { latitude: number; longitude: number };
  allPois?: Array<{
    id: string;
    latitude: number;
    longitude: number;
    name: string;
    layerName?: string;
  }>;
  showPOIs?: boolean;
  selectedPoiId?: string | null;
  mapNavTarget?: { latitude: number; longitude: number };
  onPoiClick?: (id: string) => void;
  onMapClick?: (point: { latitude: number; longitude: number }) => void;
  layerColors?: Record<string, string>;
}

const FALLBACK_LAYER_COLOR = '#0CB4E4';
const MAP_PADDING = 15;

const normalizeLayerName = (layerName?: string) => layerName?.trim() || 'Unknown';

const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (value: number) =>
    Math.round((value + m) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const stringToColor = (input: string): string => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }

  const hue = Math.abs(hash) % 360;
  return hslToHex(hue, 65, 60);
};

const getLayerColor = (
  layerName: string,
  overrides: Record<string, string>
): string => {
  return overrides[layerName] ?? stringToColor(layerName);
};

const PathNetworkMiniMap: React.FC<PathNetworkMiniMapProps> = ({
  pathNetwork,
  currentPosition,
  allPois = [],
  showPOIs = false,
  selectedPoiId = null,
  mapNavTarget,
  onPoiClick,
  onMapClick,
  layerColors = {},
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pinPixelsRef = useRef<Array<{ id: string; x: number; y: number }>>([]);

  const layers = useMemo(() => {
    const map = new Map<string, string>();

    allPois.forEach((poi) => {
      const name = normalizeLayerName(poi.layerName);
      if (!map.has(name)) {
        map.set(name, getLayerColor(name, layerColors));
      }
    });

    return map;
  }, [allPois, layerColors]);

  const bounds = useMemo(() => {
    if (!pathNetwork || pathNetwork.length === 0) {
      return null;
    }

    let minLon = Infinity;
    let maxLon = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    pathNetwork.forEach((segment) => {
      segment.forEach(([lon, lat]) => {
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });
    });

    if (
      !Number.isFinite(minLon) ||
      !Number.isFinite(maxLon) ||
      !Number.isFinite(minLat) ||
      !Number.isFinite(maxLat)
    ) {
      return null;
    }

    return { minLon, maxLon, minLat, maxLat };
  }, [pathNetwork]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bounds) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const drawPin = (x: number, y: number, size: number, color: string) => {
      const r = size;
      const tipY = y + r * 2;

      ctx.beginPath();
      ctx.arc(x, y, r, Math.PI * 0.15, Math.PI * 0.85, true);
      ctx.lineTo(x, tipY);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(x, y - r * 0.15, r * 0.35, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.fill();
    };

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;

      if (cssW === 0 || cssH === 0) {
        return;
      }

      const bufW = Math.round(cssW * dpr);
      const bufH = Math.round(cssH * dpr);

      if (canvas.width !== bufW || canvas.height !== bufH) {
        canvas.width = bufW;
        canvas.height = bufH;
      }

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, cssW, cssH);

      const drawW = cssW - MAP_PADDING * 2;
      const drawH = cssH - MAP_PADDING * 2;

      const lonRange = bounds.maxLon - bounds.minLon;
      const latRange = bounds.maxLat - bounds.minLat;

      if (lonRange <= 0 || latRange <= 0 || drawW <= 0 || drawH <= 0) {
        pinPixelsRef.current = [];
        ctx.restore();
        return;
      }

      const scaleX = (lon: number) =>
        MAP_PADDING + ((lon - bounds.minLon) / lonRange) * drawW;

      const scaleY = (lat: number) =>
        cssH - MAP_PADDING - ((lat - bounds.minLat) / latRange) * drawH;

      ctx.strokeStyle = '#6B7280';
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      pathNetwork.forEach((segment) => {
        if (segment.length < 2) return;

        ctx.beginPath();
        const [startLon, startLat] = segment[0];
        ctx.moveTo(scaleX(startLon), scaleY(startLat));

        for (let i = 1; i < segment.length; i++) {
          const [lon, lat] = segment[i];
          ctx.lineTo(scaleX(lon), scaleY(lat));
        }

        ctx.stroke();
      });

      pinPixelsRef.current = [];

      if (showPOIs && allPois.length > 0) {
        allPois.forEach((poi) => {
          const x = scaleX(poi.longitude);
          const y = scaleY(poi.latitude);
          const pinY = y - 6;
          const isSelected = poi.id === selectedPoiId;
          const layerName = normalizeLayerName(poi.layerName);
          const pinColor = layers.get(layerName) ?? FALLBACK_LAYER_COLOR;

          pinPixelsRef.current.push({ id: poi.id, x, y: pinY });

          drawPin(x, pinY, isSelected ? 5 : 4, pinColor);

          if (isSelected) {
            ctx.beginPath();
            ctx.arc(x, pinY, 9, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(253,186,89,0.8)';
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
        });
      }

      if (currentPosition) {
        const x = scaleX(currentPosition.longitude);
        const y = scaleY(currentPosition.latitude);

        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fillStyle = '#EF4444';
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      if (mapNavTarget) {
        const x = scaleX(mapNavTarget.longitude);
        const y = scaleY(mapNavTarget.latitude);

        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      ctx.restore();
    };

    draw();
  }, [
    pathNetwork,
    bounds,
    currentPosition,
    allPois,
    showPOIs,
    selectedPoiId,
    mapNavTarget,
    layers,
  ]);

  if (!pathNetwork || pathNetwork.length === 0) {
    return null;
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="w-full bg-black/40 rounded border border-white/10"
        style={{
          height: '180px',
          cursor: onPoiClick || onMapClick ? 'pointer' : 'default',
        }}
        onClick={(e) => {
          if ((!onPoiClick && !onMapClick) || !bounds) return;

          const canvas = e.currentTarget;
          const rect = canvas.getBoundingClientRect();

          const mx = e.clientX - rect.left;
          const my = e.clientY - rect.top;

          if (onPoiClick && pinPixelsRef.current.length > 0) {
            let closestId: string | null = null;
            let closestDist = Infinity;

            pinPixelsRef.current.forEach((pin) => {
              const dist = Math.hypot(pin.x - mx, pin.y - my);
              if (dist < closestDist) {
                closestDist = dist;
                closestId = pin.id;
              }
            });

            if (closestId && closestDist < 14) {
              onPoiClick(closestId);
              return;
            }
          }

          if (!onMapClick) return;

          const drawW = rect.width - MAP_PADDING * 2;
          const drawH = rect.height - MAP_PADDING * 2;

          const lonRange = bounds.maxLon - bounds.minLon;
          const latRange = bounds.maxLat - bounds.minLat;

          if (lonRange <= 0 || latRange <= 0 || drawW <= 0 || drawH <= 0) {
            return;
          }

          const clickedLon =
            bounds.minLon + ((mx - MAP_PADDING) / drawW) * lonRange;
          const clickedLat =
            bounds.minLat +
            ((rect.height - MAP_PADDING - my) / drawH) * latRange;

          onMapClick({ latitude: clickedLat, longitude: clickedLon });
        }}
      />
    </div>
  );
};

export default PathNetworkMiniMap;
